import { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { SCHOOLS } from "../data/schools";

/**
 * Typeahead over the NSC schools list (~5,900 institutions).
 *
 * - Matches against school name, city, or state (case-insensitive substring).
 * - Ranks exact prefix matches on the name above other substring matches.
 * - Suggestion row shows the name as primary text, city + state as secondary,
 *   so students at multi-campus systems (e.g. "American Intercontinental
 *   University — Atlanta, GA") can disambiguate before selecting.
 * - Free-text is still allowed for anything not in the list (per the
 *   pilot-era product decision to not block campaign creation on a missing
 *   school row).
 * - Value is stored as the school NAME string (back-compat with existing
 *   consumers that treat `school` as a plain string).
 */
export const SchoolInput = ({ value, onChange, placeholder, testid }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return SCHOOLS.slice(0, 8);
    // First: exact prefix matches on name (best signal); Then: substring anywhere
    const prefix = [];
    const contains = [];
    for (const s of SCHOOLS) {
      const n = s.name.toLowerCase();
      if (n.startsWith(q)) {
        prefix.push(s);
      } else if (
        n.includes(q) ||
        s.city.toLowerCase().includes(q) ||
        s.state.toLowerCase() === q
      ) {
        contains.push(s);
      }
      if (prefix.length >= 8) break;
    }
    return [...prefix, ...contains].slice(0, 8);
  }, [value]);

  const commit = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          autoComplete="off"
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 pr-9 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        {value ? (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => onChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-olive hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-olive" />
        )}
      </div>
      <AnimatePresence>
        {open && suggestions.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-bone shadow-lg"
            data-testid={`${testid}-suggestions`}
          >
            {suggestions.map((s) => (
              <li key={`${s.code}-${s.name}-${s.city}`}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); commit(s.name); }}
                  className="block w-full truncate px-3 py-2 text-left hover:bg-yellow/25"
                >
                  <div className="truncate text-[13px] text-ink">{s.name}</div>
                  {(s.city || s.state) && (
                    <div className="truncate text-[13px] text-olive">
                      {[s.city, s.state].filter(Boolean).join(", ")}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
